import { motion } from "framer-motion";
import { Sparkles, QrCode, Wifi, MousePointerClick, ScanLine } from "lucide-react";

const FEATURES = [
    { icon: QrCode, label: "Lead Capture", desc: "QR + form flows that convert in-venue." },
    { icon: Wifi, label: "Connected IoT", desc: "Smart booths, sensors and live data hooks." },
    { icon: MousePointerClick, label: "Touch Walls", desc: "Interactive surfaces that drive dwell." },
    { icon: ScanLine, label: "AR / Vision", desc: "Camera-based product try-ons and games." },
];

const ACTIVATION_VIDEOS = [
    { id: "0sPdrf2UoXA", title: "AE Interactive Activation — Reel 1" },
    { id: "Ujxc1vrdQCo", title: "AE Interactive Activation — Reel 2" },
];

export default function Activations() {
    return (
        <section
            id="activations"
            data-testid="activations-section"
            className="relative py-24 md:py-32 bg-ae-gray-100 overflow-hidden"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    {/* Visual — embedded YouTube activation reels */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="relative rounded-3xl p-4 sm:p-5 bg-gradient-to-br from-ae-ink to-ae-gray-700 shadow-ae-lift"
                        data-testid="activations-visual"
                    >
                        <div className="flex items-center justify-between px-1 pb-3">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-md text-white text-xs font-semibold uppercase tracking-widest">
                                <Sparkles className="h-3.5 w-3.5 text-ae-orange-light" />
                                Live Activation
                            </div>
                            <div className="text-white/80 text-xs font-mono">#AE · 2026</div>
                        </div>

                        <div className="space-y-4">
                            {ACTIVATION_VIDEOS.map((v, i) => (
                                <motion.div
                                    key={v.id}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.6, delay: i * 0.12 }}
                                    data-testid={`activation-video-${i}`}
                                    className="relative aspect-video rounded-2xl overflow-hidden border border-white/15 bg-black shadow-ae-soft ring-1 ring-ae-orange/20"
                                >
                                    <iframe
                                        src={`https://www.youtube.com/embed/${v.id}?rel=0&modestbranding=1`}
                                        title={v.title}
                                        loading="lazy"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        allowFullScreen
                                        className="absolute inset-0 w-full h-full"
                                    />
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Copy */}
                    <div>
                        <div className="inline-flex items-center gap-2 text-ae-orange text-xs font-bold uppercase tracking-[0.22em]">
                            <Sparkles className="h-4 w-4" /> Interactive Activations
                        </div>
                        <h2 className="mt-5 font-display font-extrabold text-4xl md:text-5xl lg:text-6xl text-ae-ink tracking-tight leading-[1.02]">
                            Digitally enhanced brand experiences —{" "}
                            <span className="text-ae-orange">our specialty</span>.
                        </h2>
                        <p className="mt-6 text-lg text-ae-gray-700 leading-relaxed max-w-xl">
                            We design and build experiential moments that fuse creative
                            craft, software and data — turning crowds into customers and
                            attention into measurable action.
                        </p>

                        <div className="mt-10 grid sm:grid-cols-2 gap-4">
                            {FEATURES.map((f, i) => (
                                <motion.div
                                    key={f.label}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.6, delay: i * 0.08 }}
                                    data-testid={`activation-feature-${i}`}
                                    className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-black/5 shadow-ae-soft hover:shadow-ae-lift hover:border-ae-orange/30 transition-all duration-500"
                                >
                                    <div className="h-11 w-11 rounded-xl bg-ae-orange/10 text-ae-orange flex items-center justify-center shrink-0">
                                        <f.icon className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <div className="font-display font-bold text-ae-ink">
                                            {f.label}
                                        </div>
                                        <div className="text-sm text-ae-gray-700 mt-1">
                                            {f.desc}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
